<template>
  <div>
    <CRow>
      <CCol sm="9" class="h-100">
        <h4 class>
          {{ $t('settings.security.component.twoFactor.title') }}
        </h4>
        <div class="border-line"></div>
      </CCol>
    </CRow>

    <CRow class="mt-2 py-2">
      <CCol class="font-weight-semi-bold">
        <CRow>
          <CCol sm="1">
            <CIcon :name="nameIcon" :class="classIcon" size="custom-size" :height="30" viewBox="0 0 32 32" />
          </CCol>
          <CCol v-if="phoneNumberConfirmed === false">
            {{ $t('settings.security.component.twoFactor.noPhoneNumberValidated') }}
          </CCol>
          <CCol v-else>
            {{ $t('settings.security.component.twoFactor.text') }}
          </CCol>
        </CRow>
      </CCol>
      <CCol>
        <CSwitch
          class="switch-custom"
          color="primary"
          shape="pill"
          size="lg"
          :checked.sync="requiresSMS2FA"
          :disabled="canSwitch"
          @update:checked="changeMFAConfiguration()"
        />
      </CCol>
    </CRow>

    <transition name="fade" v-if="confirmationPopup">
      <div class="dataTable__popUp popUp" style="margin-top: -240px">
        <div class="popUp__body shadow-sm">
          <div class="">
            <div class="text-gray-600 mb-2">
              {{ $t('settings.security.component.twoFactor.validation.text') }}
            </div>
            <div>
              <CInput v-model="confirmationCode" />
            </div>
            <div class="pb-4 text-right" style="text-decoration: underline">
              <CLink v-if="!resendLoading" @click="resendMFACode()">{{ $t('settings.security.component.twoFactor.validation.link') }} </CLink>
              <LoadingSpinner v-else size="sm" />
            </div>
            <div>
              <CButton color="white" class="py-2 px-5 mr-2" @click="cancelConfirmation()">
                {{ $t('settings.security.component.phoneNumber.popup.button.cancel') }}
              </CButton>
              <CButton :disabled="!isValidatable" color="primary" class="py-2 px-5" @click="validateMFACode()">
                {{ $t('settings.security.component.twoFactor.validation.button') }}
              </CButton>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import services from '@/services.js'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

const mfaPurposeEnum = {
  LOGIN: 0, // Não é usado nesta tela. Deixado apenas para referência.
  ENABLE_SMS_2FA: 1,
  DISABLE_SMS_2FA: 2,
}

const mfaProvidersEnum = {
  SMS: 0,
  EMAIL: 1,
  AUTHENTICATOR_APP: 2,
}

export default {
  name: 'twoFactorAuthentication',
  props: {
    phoneNumberConfirmed: {
      type: Boolean,
      default: undefined,
    },
  },
  components: {
    LoadingSpinner,
  },
  data() {
    return {
      isMFAEnabled: false,
      requiresSMS2FA: false,
      loading: false,
      loadingValidation: false,
      confirmationCode: '',
      confirmationPopup: false,
      resendLoading: false,
    }
  },
  computed: {
    canSwitch() {
      if (!this.phoneNumberConfirmed) return true
      return this.loading
    },
    classIcon() {
      if (this.phoneNumberConfirmed === false) {
        return 'text-error'
      }
      if (this.isMFAEnabled) {
        return 'text-success'
      } else {
        return 'text-warning'
      }
    },
    nameIcon() {
      if (this.phoneNumberConfirmed === false) {
        return 'notificationAlert'
      }
      if (this.isMFAEnabled) {
        return 'checkCircle'
      } else {
        return 'infoCircle'
      }
    },
    isValidatable() {
      return this.confirmationCode.length > 2 && !this.loadingValidation
    },
  },
  mounted() {
    this.getMFA()
  },
  methods: {
    getMFA() {
      this.loading = true

      let userID = this.$store.state.auth.user.id

      services
        .getMFA(userID)
        .then((res) => {
          this.requiresSMS2FA = this.isMFAEnabled = res.data.data.providers.find((elm) => elm.provider == mfaProvidersEnum.SMS).isEnabled
        })
        .catch(({ response }) => {
          this.$emit('showMessage', {
            status: 'danger',
            text: response.data.message,
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    changeMFAConfiguration() {
      let params = {
        requiresSMS2FA: this.requiresSMS2FA,
      }
      this.loading = true

      services
        .postMFA(params)
        .then((res) => {
          this.confirmationCode = ''
          this.confirmationPopup = true
        })
        .catch(({ response }) => {
          this.$emit('showMessage', {
            status: 'danger',
            text: response.data.message,
          })
          this.requiresSMS2FA = !this.requiresSMS2FA
          this.loading = false
        })
    },
    resendMFACode() {
      let params = {
        userId: this.$store.state.auth.user.id,
        purpose: this.requiresSMS2FA ? mfaPurposeEnum.ENABLE_SMS_2FA : mfaPurposeEnum.DISABLE_SMS_2FA,
      }

      this.resendLoading = true

      services
        .postMFAResend(params)
        .then((res) => {})
        .catch(({ response }) => {
          this.$emit('showMessage', {
            status: 'danger',
            text: response.data.message,
          })
        })
        .finally(() => setTimeout(() => (this.resendLoading = false), 2000))
    },
    cancelConfirmation() {
      this.confirmationPopup = false
      this.requiresSMS2FA = !this.requiresSMS2FA
      this.loading = false
    },
    closeConfirmation() {
      this.confirmationPopup = false
      this.loading = false
      this.getMFA()
    },
    showConfirmation() {
      this.confirmationPopup = true
      this.confirmationCode = ''
      this.loadingValidation = false
    },
    validateMFACode() {
      this.loadingValidation = true

      let params = {
        token: this.confirmationCode,
        requiresSMS2FA: this.requiresSMS2FA,
      }
      services
        .postMFAValidate(params)
        .then((res) => {
          this.closeConfirmation()
          this.confirmationCode = ''
          this.$emit('showMessage', {
            status: 'success',
            text: this.$t('settings.security.component.twoFactor.message.successValidation'),
          })
        })
        .catch(({ response }) => {
          this.$emit('showMessage', {
            status: 'danger',
            text: this.$t('general.errors.' + response.data.message),
          })
        })
        .finally(() => (this.loadingValidation = false))
    },
  },
}
</script>

<style lang="scss">
.switch-custom span {
  background-color: $gray-500;
}
</style>
